import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FreshnessBadge, FxBadge, sourceLabel } from '@/components/Freshness';
import { Header } from '@/components/Header';
import { IconChevronDown, IconChevronRight, IconPlus, IconTrash } from '@/components/Icons';
import { PROFILES } from '@/data/catalog';
import { AddAssetDialog } from '@/features/addAsset/AddAssetDialog';
import { exportBackup } from '@/features/settings/backup';
import { holdingLabel, useT } from '@/i18n';
import { cx } from '@/lib/cx';
import { formatMoney, formatMoneyParts, formatPct, formatPrice, formatQty, formatRate } from '@/lib/money';
import { formatDateTime, formatShortDate } from '@/lib/time';
import { exchangeLabel } from '@/market/exchange';
import { TEMPLATES, templateName } from '@/reports/templates';
import { computeTotals, convertFromUsd, valueAsset } from '@/market/value';
import { hasQuoteSource, useSettings } from '@/settings/settingsStore';
import { appStore, useAppState } from '@/state/appStore';
import { isWatched, quoteKey, type Asset, type Portfolio, type Report } from '@/types';
import s from './list.module.css';

export function ListView() {
  const { t } = useT();
  const app = useAppState();
  const settings = useSettings();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState<{ portfolioId: string } | null>(null);
  const [newName, setNewName] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(null);
  const ccy = settings.displayCurrency;
  const totals = useMemo(() => computeTotals(app.assets, app.quotes, app.fx), [app.assets, app.quotes, app.fx]);
  const display = (usd: number | null | undefined) => (usd == null ? null : convertFromUsd(usd, ccy, app.fx));
  const dayPct = totals.dayChangeKnownUsd > 0 ? (totals.dayChangeUsd / (totals.dayChangeKnownUsd - totals.dayChangeUsd)) * 100 : null;

  const equityQuote = app.assets.filter((a) => a.assetClass === 'stock' || a.assetClass === 'etf').map((a) => app.quotes[quoteKey(a)]).find(Boolean);
  const cryptoQuote = app.assets.filter((a) => a.assetClass === 'crypto').map((a) => app.quotes[quoteKey(a)]).find(Boolean);

  const latestReport = useMemo(() => Object.values(app.reports).filter((r): r is Report => !!r).sort((a, b) => b.generatedAt - a.generatedAt)[0], [app.reports]);
  const latestThread = app.threads[0];
  const total = formatMoneyParts(display(totals.totalUsd), ccy);

  const toggle = (id: string) =>
    setCollapsed((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const createPortfolio = async () => {
    const name = (newName ?? '').trim();
    if (!name) return;
    await appStore.createPortfolio(name);
    setNewName(null);
  };

  const deletePortfolio = async (p: Portfolio, count: number) => {
    if (!window.confirm(t('list.confirmDeletePortfolio', { name: p.name, n: count }))) return;
    await appStore.deletePortfolio(p.id);
  };

  const commitRename = async () => {
    if (renaming) await appStore.renamePortfolio(renaming.id, renaming.value);
    setRenaming(null);
  };

  if (app.ready && app.portfolios.length === 0) {
    return (
      <div className="page">
        <Header onExport={exportBackup} showCurrency={false} />
        <div className={s.empty}>
          <h2 className={s.emptyTitle}>{t('list.emptyTitle')}</h2>
          <p className="muted" style={{ margin: 0, lineHeight: 1.6 }}>{t('list.emptyBody')}</p>
          <div className={s.newInline}>
            <input className="input" placeholder={t('list.portfolioName')} value={newName ?? ''} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void createPortfolio()} autoFocus />
            <button type="button" className="btn primary" onClick={() => void createPortfolio()} disabled={!(newName ?? '').trim()}>{t('list.newPortfolio')}</button>
          </div>
          {!hasQuoteSource(settings) && (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>{t('list.emptyNoKey')} <Link to="/settings#data">{t('list.goSetup')}</Link></p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <Header onExport={exportBackup} />

      <section className={s.hero}>
        <div>
          <div className="label">{t('list.total')}</div>
          <div className={`serif num ${s.total}`}>
            {total.main}
            <span className={`muted ${s.totalCents}`}>{total.tail}</span>
          </div>
          <div className={s.heroMeta}>
            {dayPct != null && (
              <span className={cx('num', s.dayChange, totals.dayChangeUsd >= 0 ? 'up' : 'down')}>
                {t('list.dayChange', { amount: formatMoney(display(totals.dayChangeUsd), ccy, { sign: true }), pct: formatPct(dayPct) })}
              </span>
            )}
            {equityQuote && <FreshnessBadge quote={equityQuote} withSource />}
            {cryptoQuote && <FreshnessBadge quote={cryptoQuote} withSource />}
            <FxBadge date={app.fx?.date} />
            {totals.missing > 0 && <span className="badge"><i className="dot off" />{t('list.missing', { n: totals.missing })}</span>}
          </div>
        </div>
        <div className={s.feats}>
          <div className={s.featLabel}>
            <span className="label">{t('list.featLabel')}</span>
            <span className="muted" style={{ fontSize: 11 }}>{t('list.featOptional')}</span>
          </div>
          <div className={s.featGrid}>
            <Link to="/reports" className={s.feat}>
              <div className={s.featHead}>
                <span className={`serif ${s.featTitle}`}>{t('reports.title')}</span>
                <span className="muted" style={{ fontSize: 11 }}>{t('list.featReportsCount', { n: TEMPLATES.length })}</span>
              </div>
              <p className={s.featBody}>{t('list.featReportsBody')}</p>
              <div className={s.featFoot}>
                <span className="muted num">{latestReport ? t('list.featLast', { what: `${templateName(latestReport.templateId)} · ${formatDateTime(latestReport.generatedAt)}` }) : t('list.featNone')}</span>
                <span style={{ color: 'var(--link)' }}>{t('list.open')}</span>
              </div>
            </Link>
            <Link to="/experts" className={s.feat}>
              <div className={s.featHead}>
                <span className={`serif ${s.featTitle}`}>{t('list.experts')}</span>
                <span className="muted" style={{ fontSize: 11 }}>{t('list.featExpertsCount', { n: PROFILES.length })}</span>
              </div>
              <p className={s.featBody}>{t('list.featExpertsBody')}</p>
              <div className={s.featFoot}>
                <span className="muted num">{latestThread ? t('list.featLast', { what: `${latestThread.title} · ${formatDateTime(latestThread.updatedAt)}` }) : t('list.featNone')}</span>
                <span style={{ color: 'var(--link)' }}>{t('list.open')}</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <div className={`muted ${s.cols} ${s.head}`}>
        <span>{t('list.col.asset')}</span>
        <span className={s.r}>{t('list.col.qty')}</span>
        <span className={s.r}>{t('list.col.cost')}</span>
        <span className={s.r}>{t('list.col.price')}</span>
        <span className={s.r}>{t('list.col.value', { ccy })}</span>
        <span className={s.r}>{t('list.col.day')}</span>
        <span className={s.r}>{t('list.col.data')}</span>
      </div>

      {app.portfolios.map((p) => {
        const assets = app.assets.filter((a) => a.portfolioId === p.id);
        const subtotal = totals.byPortfolio[p.id] ?? 0;
        const share = totals.totalUsd > 0 ? (subtotal / totals.totalUsd) * 100 : 0;
        const known = assets.reduce((acc, a) => {
          const v = valueAsset(a, app.quotes, app.fx);
          if (v.valueUsd != null && v.quote?.changePct != null && a.assetClass !== 'cash') {
            acc.value += v.valueUsd;
            acc.prev += v.valueUsd / (1 + v.quote.changePct / 100);
          }
          return acc;
        }, { value: 0, prev: 0 });
        const groupPct = known.prev > 0 ? ((known.value - known.prev) / known.prev) * 100 : null;
        const isCollapsed = collapsed.has(p.id);
        const sources = Array.from(new Set(assets.map((a) => app.quotes[quoteKey(a)]?.source).filter((x) => x && x !== 'fixed')));

        return (
          <section key={p.id}>
            <div className={s.group}>
              <div className={s.groupName}>
                <button type="button" className={s.caret} onClick={() => toggle(p.id)} aria-expanded={!isCollapsed}>
                  {isCollapsed ? <IconChevronRight /> : <IconChevronDown />}
                </button>
                {renaming?.id === p.id ? (
                  <input className={s.renameInput} value={renaming.value} autoFocus onChange={(e) => setRenaming({ id: p.id, value: e.target.value })} onBlur={() => void commitRename()} onKeyDown={(e) => { if (e.key === 'Enter') void commitRename(); if (e.key === 'Escape') setRenaming(null); }} />
                ) : (
                  <button type="button" className={`serif ${s.groupTitle} ${s.groupTitleBtn}`} title={t('list.renameHint')} onClick={() => setRenaming({ id: p.id, value: p.name })}>{p.name}</button>
                )}
                <span className="muted" style={{ fontSize: 12 }}>{totals.totalUsd > 0 ? t('list.itemsShare', { n: assets.length, pct: formatPct(share, false) }) : t('list.items', { n: assets.length })}</span>
              </div>
              <span className={s.gap} /><span className={s.gap} /><span className={s.gap} />
              <span className={`${s.r} serif num ${s.groupSub}`}>{formatMoney(display(subtotal), ccy)}</span>
              <span className={cx(s.r, 'num', s.groupPct, groupPct == null ? 'muted' : groupPct >= 0 ? 'up' : 'down')} style={{ fontWeight: 500 }}>{groupPct == null ? '—' : formatPct(groupPct)}</span>
              <div className={s.groupActions}>
                <span className={`muted ${s.groupSources}`}>{sources.map((x) => sourceLabel(x!)).join(' · ')}</span>
                <button type="button" className={s.iconBtn} title={t('list.addAsset')} onClick={() => setAdding({ portfolioId: p.id })}><IconPlus /></button>
                <button type="button" className={s.iconBtn} title={t('list.deletePortfolio')} onClick={() => void deletePortfolio(p, assets.length)}><IconTrash /></button>
              </div>
            </div>
            {!isCollapsed && assets.length === 0 && (
              <div className={`muted ${s.emptyRow}`}>
                <span>{t('list.emptyPortfolio')}</span>
                <button type="button" className="btn quiet" onClick={() => setAdding({ portfolioId: p.id })}><IconPlus size={12} />{t('list.addAsset')}</button>
              </div>
            )}
            {!isCollapsed && assets.map((a) => <AssetRow key={a.id} asset={a} ccy={ccy} />)}
          </section>
        );
      })}

      <div className={s.newInline}>
        {newName == null ? (
          <button type="button" className="btn quiet" onClick={() => setNewName('')}><IconPlus size={12} />{t('list.newPortfolio')}</button>
        ) : (
          <>
            <input className="input" placeholder={t('list.portfolioName')} value={newName} autoFocus onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void createPortfolio(); if (e.key === 'Escape') setNewName(null); }} />
            <button type="button" className="btn primary" onClick={() => void createPortfolio()} disabled={!newName.trim()}>{t('list.create')}</button>
            <button type="button" className="btn quiet" onClick={() => setNewName(null)}>{t('common.cancel')}</button>
          </>
        )}
      </div>

      <footer className={s.footer}>
        <span className={`muted ${s.footerNote}`}>{t('list.footerNote')}</span>
        <nav className={s.footerLinks} aria-label={t('about.title')}>
          <Link to="/about">{t('footer.about')}</Link>
          <Link to="/about#privacy">{t('footer.privacy')}</Link>
          <Link to="/about#terms">{t('footer.terms')}</Link>
        </nav>
      </footer>

      {adding && <AddAssetDialog portfolioId={adding.portfolioId} onClose={() => setAdding(null)} />}
    </div>
  );
}

function AssetRow({ asset, ccy }: { asset: Asset; ccy: string }) {
  const { t } = useT();
  const app = useAppState();
  const v = valueAsset(asset, app.quotes, app.fx);
  const value = v.valueUsd == null ? null : convertFromUsd(v.valueUsd, ccy, app.fx);
  const isCash = asset.assetClass === 'cash';
  const cashCcy = asset.currency.toUpperCase();
  const price = isCash ? formatRate(v.priceUsd) : formatPrice(v.quote?.price ?? asset.manualPrice ?? null);
  const change = isCash ? null : v.quote?.changePct;
  const watched = isWatched(asset);

  return (
    <Link to={`/a/${asset.id}`} className={s.row}>
      <span className={s.name}>
        <span className={s.symbol}>
          {isCash ? t('list.cashName', { ccy: cashCcy }) : asset.symbol.toUpperCase()}
          {!isCash && <span className="muted" style={{ fontWeight: 400, marginLeft: 6 }}>{asset.name}</span>}
          {watched && <span className="badge" style={{ marginLeft: 8, fontWeight: 400 }}>{t('list.watch')}</span>}
        </span>
        <span className={`muted ${s.sub}`}>
          {isCash
            ? t('list.cashSub', { amount: formatQty(asset.quantity), ccy: cashCcy }) + (v.priceUsd ? t('list.cashRate', { rate: formatRate(v.priceUsd) }) : '')
            : [exchangeLabel(asset.mic ?? asset.exchange), holdingLabel(asset), asset.note].filter(Boolean).join(' · ')}
        </span>
      </span>
      {/* .trail is display: contents on desktop (three grid cells) and one flowing line on phones. */}
      <span className={s.trail}>
        <span className={`${s.r} num ${s.cQty}`}><span className={`muted ${s.lbl}`}>{t('list.col.qty')} </span>{watched ? '—' : formatQty(asset.quantity)}</span>
        <span className={`${s.r} num muted ${s.cCost}`}><span className={`muted ${s.lbl}`}>{t('list.col.cost')} </span>{asset.costPrice != null && !isCash ? formatPrice(asset.costPrice) : '—'}</span>
        <span className={`${s.r} num ${s.cPrice}`}><span className={`muted ${s.lbl}`}>{t('list.col.price')} </span>{price}{!isCash && asset.currency.toUpperCase() !== 'USD' && <span className="muted" style={{ fontSize: 11, marginLeft: 4 }}>{asset.currency.toUpperCase()}</span>}</span>
      </span>
      <span className={`${s.r} num ${s.value} ${s.cValue}`}>{watched ? <span className="muted">—</span> : formatMoney(value, ccy)}</span>
      <span className={cx(s.r, 'num', s.cDay, change == null ? 'muted' : change >= 0 ? 'up' : 'down')}>{change == null ? '—' : formatPct(change)}</span>
      <span className={`${s.r} ${s.cData}`}>
        {isCash ? (
          <span className="badge"><i className={app.fx ? 'dot close' : 'dot off'} />{cashCcy === 'USD' ? t('fresh.base') : app.fx ? t('detail.ecb', { date: app.fx.date.slice(5) }) : t('fresh.fxNone')}</span>
        ) : (
          <FreshnessBadge quote={v.quote} fallback={asset.manualPrice ? (asset.manualPriceAt ? t('fresh.manualDated', { date: formatShortDate(asset.manualPriceAt) }) : t('fresh.manual')) : t('fresh.none')} />
        )}
      </span>
    </Link>
  );
}
